import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { DealsService } from 'src/services/data/deals.service'
import { OnDestroyMixin } from '@w11k/ngx-componentdestroyed'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import { untilComponentDestroyed } from '@w11k/ngx-componentdestroyed'

/** Normalized deal view shape used by this dialog: deal + parties. Callers must pass this shape (e.g. from getDealViews or by mapping list rows). */
export interface DealReferenceNumbersDealView {
  deal: {
    deal_id: string
    attributes?: { supplier_ref?: string; buyer_ref?: string }
    supplier_id?: number | string
    buyer_id?: number | string
  }
  parties?: readonly { account?: number | string; name?: string }[]
}

export interface DealReferenceNumbersOptions {
  dealIds: string[]
  /** When provided (e.g. from deal list), no extra getDealViews call is made. */
  dealViews?: DealReferenceNumbersDealView[]
}

export interface DealReferenceRow {
  dealId: string
  supplier: string
  supplierRefNumber: string
  buyer: string
  buyerRefNumber: string
}

@Component({
  selector: 'tc-deal-reference-numbers',
  templateUrl: './deal-reference-numbers.component.html',
  styleUrls: ['./deal-reference-numbers.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class DealReferenceNumbersComponent extends OnDestroyMixin implements OnInit {
  private readonly dialogData = inject<DealReferenceNumbersOptions>(MAT_DIALOG_DATA)
  private dialogRef = inject<MatDialogRef<DealReferenceNumbersComponent>>(MatDialogRef)
  private Deals = inject(DealsService)
  private cdr = inject(ChangeDetectorRef)
  private toaster = inject(ToasterService)

  deals: DealReferenceRow[] = []
  loading = true
  private initialRefs: { dealId: string; supplierRefNumber: string; buyerRefNumber: string }[] = []

  ngOnInit() {
    if (this.dialogData.dealViews?.length) {
      this.applyDealViews(this.dialogData.dealViews)
      return
    }
    this.Deals.getDealViews(this.dialogData.dealIds, ['deal', 'parties'])
      .pipe(untilComponentDestroyed(this))
      .subscribe({
        next: (dealViews) => this.applyDealViews(dealViews as DealReferenceNumbersDealView[]),
        error: () => {
          this.toaster.error('Failed to load reference numbers')
          this.loading = false
          this.deals = []
          this.cdr.markForCheck()
        },
      })
  }

  private applyDealViews(dealViews: DealReferenceNumbersDealView[]) {
    const idToView = new Map(dealViews.map((dv) => [String(dv.deal.deal_id), dv]))
    this.deals = this.dialogData.dealIds.map((dealId) => {
      const dv = idToView.get(String(dealId))
      const deal = dv?.deal
      const parties = dv?.parties ?? []
      const supplierParty = parties.find((p: { account?: number | string }) => String(p?.account) === String(deal?.supplier_id))
      const buyerParty = parties.find((p: { account?: number | string }) => String(p?.account) === String(deal?.buyer_id))
      return {
        dealId,
        supplier: supplierParty?.name ?? '',
        supplierRefNumber: deal?.attributes?.supplier_ref ?? '',
        buyer: buyerParty?.name ?? '',
        buyerRefNumber: deal?.attributes?.buyer_ref ?? '',
      }
    })
    this.initialRefs = this.deals.map((d) => ({
      dealId: d.dealId,
      supplierRefNumber: d.supplierRefNumber,
      buyerRefNumber: d.buyerRefNumber,
    }))
    this.loading = false
    this.cdr.markForCheck()
  }

  get hasChanges(): boolean {
    if (this.deals.length !== this.initialRefs.length) return true
    return this.deals.some((row) => {
      const initial = this.initialRefs.find((r) => r.dealId === row.dealId)
      return !initial || initial.supplierRefNumber !== row.supplierRefNumber || initial.buyerRefNumber !== row.buyerRefNumber
    })
  }

  cancel() {
    this.dialogRef.close()
  }

  update() {
    // get the deals that have changes
    const dealsToUpdate = this.deals.filter((deal) => {
      const initial = this.initialRefs.find((r) => r.dealId === deal.dealId)
      return !initial || initial.supplierRefNumber !== deal.supplierRefNumber || initial.buyerRefNumber !== deal.buyerRefNumber
    })
    if (dealsToUpdate.length > 0) {
      this.loading = true
      this.cdr.markForCheck()
      this.Deals.getDealsByIds(dealsToUpdate.map((d) => d.dealId))
        .then((dealsById) => {
          return Promise.all(
            dealsToUpdate.map((dealToUpdate) => {
              const deal = dealsById[dealToUpdate.dealId] ?? dealsById[String(dealToUpdate.dealId)];
              if (!deal) {
                console.warn(`Deal ${dealToUpdate.dealId} not found for update`)
                return Promise.resolve();
              }
              return this.Deals.patchImmutableDeal(
                deal,
                {
                  attributes: {
                    supplier_ref: dealToUpdate.supplierRefNumber,
                    buyer_ref: dealToUpdate.buyerRefNumber,
                  },
                },
                { skip_calculations: 1 }
              );
            })
          ).then(() => {
            this.toaster.success('Reference numbers updated successfully')
            this.dialogRef.close(this.deals)
          }).catch(() => {
            this.toaster.error('Failed to update reference numbers')
          })
        })
        .catch(() => {
          this.toaster.error('Failed to load deals for update')
        })
        .finally(() => {
          this.loading = false
          this.cdr.markForCheck()
        })
    } else {
      this.toaster.info('No changes to update')
    }
  }
}